import React from 'react';
import { Layout, Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  MonitorOutlined,
  HistoryOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  ApiOutlined,
  ReconciliationOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useApp } from '../../contexts/AppContext';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useApp();

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '总览',
    },
    {
      key: '/monitor',
      icon: <MonitorOutlined />,
      label: '监控管理',
      children: [
        {
          key: '/monitor/urls',
          icon: <ApiOutlined />,
          label: 'URL管理',
        },
        {
          key: '/monitor/logs',
          icon: <HistoryOutlined />,
          label: '监控日志',
        },
      ],
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: '通知管理',
      children: [
        {
          key: '/notifications/config',
          icon: <SettingOutlined />,
          label: '通知配置',
        },
        {
          key: '/notifications/logs',
          icon: <ReconciliationOutlined />,
          label: '通知记录',
        },
      ],
    },
    {
      key: '/profile',
      icon: <UserOutlined />,
      label: '个人设置',
    },
    // 管理员菜单（仅管理员可见）
    ...(state.user?.role === 'admin' ? [{
      key: '/admin',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/admin/users',
          icon: <TeamOutlined />,
          label: '用户管理',
        },
        {
          key: '/admin/settings',
          icon: <SettingOutlined />,
          label: '系统设置',
        },
      ],
    }] : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  // 获取当前选中的菜单项
  const getSelectedKeys = () => {
    const path = location.pathname;
    
    // 精确匹配
    if (menuItems.some(item => item.key === path)) {
      return [path];
    }
    
    // 匹配子菜单
    for (const item of menuItems) {
      if (item.children) {
        const childMatch = item.children.find(child => child.key === path);
        if (childMatch) {
          return [childMatch.key];
        }
      }
    }
    
    // 默认选中dashboard
    return ['/dashboard'];
  };

  // 获取展开的菜单项
  const getOpenKeys = () => {
    const path = location.pathname;
    
    for (const item of menuItems) {
      if (item.children) {
        const hasMatch = item.children.some(child => child.key === path);
        if (hasMatch) {
          return [item.key];
        }
      }
    }
    
    return [];
  };

  return (
    <Sider 
      trigger={null} 
      collapsible 
      collapsed={collapsed}
      theme="dark"
      width={240}
      style={{
        overflow: 'auto',
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
      }}
    >
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '0' : '0 24px',
          background: 'rgba(255, 255, 255, 0.1)',
          marginBottom: 1,
        }}
      >
        {collapsed ? (
          <img 
            src="/logo.svg" 
            alt="门神" 
            style={{ 
              width: 32, 
              height: 32,
            }} 
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img 
              src="/logo.svg" 
              alt="门神" 
              style={{ 
                width: 32, 
                height: 32, 
                marginRight: 12,
              }} 
            />
            <div
              style={{
                color: '#fff',
                fontSize: 18,
                fontWeight: 'bold',
              }}
            >
              门神监控系统
            </div>
          </div>
        )}
      </div>
      
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={getSelectedKeys()}
        defaultOpenKeys={getOpenKeys()}
        items={menuItems}
        onClick={handleMenuClick}
        style={{
          border: 'none',
        }}
      />
    </Sider>
  );
};

export default Sidebar;